// About.js
import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Target, Award, Clock, Shield, Stethoscope, Building2 } from 'lucide-react';

const About = () => {
  const stats = [
    { value: '10,000+', label: 'Registered Patients', icon: Users },
    { value: '500+', label: 'Verified Doctors', icon: Stethoscope },
    { value: '150+', label: 'Partner Hospitals', icon: Building2 },
    { value: '200+', label: 'Pharmacies', icon: Heart },
    { value: '50+', label: 'Ambulances', icon: Clock },
    { value: '98%', label: 'Satisfaction Rate', icon: Award }
  ];

  const values = [
    {
      icon: Heart,
      title: 'Patient-First Approach',
      description: 'Connecting patients with verified healthcare providers for quality medical care, consultations, and emergency services.'
    },
    {
      icon: Shield,
      title: 'Verified Professionals',
      description: 'All doctors, hospitals, and pharmacies are thoroughly verified and licensed before joining our platform.'
    },
    {
      icon: Target,
      title: 'Digital Innovation',
      description: 'Leveraging technology to make healthcare accessible through online consultations, digital prescriptions, and real-time ambulance tracking.'
    }
  ];

  const features = [
    {
      title: 'For Patients',
      items: [
        'Book online consultations with verified doctors',
        'Order medicines from registered pharmacies',
        'Request emergency ambulance services',
        'Access medical history and prescriptions',
        'Find nearby hospitals and clinics'
      ]
    },
    {
      title: 'For Doctors',
      items: [
        'Register and verify professional credentials',
        'Offer online and in-person consultations',
        'Manage patient appointments digitally',
        'Write digital prescriptions',
        'Build your online medical practice'
      ]
    },
    {
      title: 'For Healthcare Providers',
      items: [
        'Register hospitals and clinics',
        'List pharmacy services and inventory',
        'Manage ambulance fleet',
        'Receive emergency requests',
        'Track service analytics'
      ]
    }
  ];

  const process = [
    { step: '1', title: 'Register', description: 'Healthcare providers register and get verified on our platform' },
    { step: '2', title: 'List Services', description: 'Doctors, hospitals, pharmacies list their services and availability' },
    { step: '3', title: 'Connect', description: 'Patients find and connect with healthcare providers instantly' },
    { step: '4', title: 'Receive Care', description: 'Get consultations, medicines delivered, or emergency services' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary-600 to-primary-700 text-white py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-center">
            About MediGo
          </h1>
          <p className="text-xl text-center max-w-3xl mx-auto text-primary-50">
            Your trusted online healthcare platform connecting patients with verified doctors, 
            hospitals, pharmacies, and emergency services across Kenya.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/auth/register" className="bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-primary-50 transition">
              Join as Patient
            </Link>
            <Link to="/auth/register" className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition">
              Register as Doctor
            </Link>
            <Link to="/auth/register" className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition">
              Register as Provider
            </Link>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12">
          <div className="bg-white rounded-2xl shadow-lg p-8 border-l-4 border-primary-500">
            <h2 className="text-3xl font-bold mb-4 text-secondary-800">Our Mission</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              To revolutionize healthcare accessibility in Kenya by creating a comprehensive digital platform 
              where healthcare providers can register and offer their services, while patients can easily 
              find, connect with, and receive quality healthcare services.
            </p>
            <p className="text-gray-600 leading-relaxed">
              We bridge the gap between healthcare seekers and providers through technology, ensuring 
              timely medical attention, transparent services, and improved health outcomes for all.
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-8 border-l-4 border-primary-500">
            <h2 className="text-3xl font-bold mb-4 text-secondary-800">Our Vision</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              To become Africa's leading digital healthcare ecosystem where every healthcare provider 
              is digitally connected and every patient has instant access to quality medical services.
            </p>
            <p className="text-gray-600 leading-relaxed">
              We envision a future where geographical barriers to healthcare are eliminated, and everyone 
              can receive medical consultation, emergency services, and medication delivery at their fingertips.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-secondary-800">
            How MediGo Works
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-secondary-800">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2">
                    <svg className="w-6 h-6 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-secondary-800">
            Our Growing Community
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
                    <Icon className="w-8 h-8 text-primary-600" />
                  </div>
                  <div className="text-2xl font-bold text-secondary-800">{stat.value}</div>
                  <div className="text-gray-600 text-sm mt-1">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-secondary-800">
            Platform Features
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-semibold mb-4 text-primary-600">{feature.title}</h3>
                <ul className="space-y-3">
                  {feature.items.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <svg className="w-5 h-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-600 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-secondary-800">
            Our Core Values
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="w-14 h-14 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-primary-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-secondary-800">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Join MediGo Today</h2>
          <p className="text-xl mb-8 text-primary-50">
            Whether you're a patient seeking healthcare or a medical professional looking to expand your practice online
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-primary-50 transition"
            >
              Get Started
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;